import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ServiceStatus } from '@/models/enums/service';
import Service from '@/services';
import { mergeObjects } from '@/utils/utils';
import { FilterModel, MapCustomerModel } from '@/models/classes/management';

const service = new Service.ManagementService();

// Interface
interface ManagementState {
  customer: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: MapCustomerModel[];
    total?: number;
  };
  filter: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: FilterModel[];
    total?: number;
  };
}

// Initialize State
const initialState: ManagementState = {
  customer: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
    total: 0,
  },
  filter: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
    total: 0,
  },
};

// Reducer
export const slice = createSlice({
  name: 'ManagementMap',
  initialState,
  reducers: {
    reset: () => initialState,
    patch: (state, action) => mergeObjects({ ...state }, action.payload),
  },
  extraReducers: (builder) => {
    // Fetch Filter
    builder.addCase(onFetchFilterManagementView.pending, (state) => {
      state.filter.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchFilterManagementView.fulfilled, (state, action) => {
      state.filter.network.code = ServiceStatus.succeeded;
      if (action.payload != null) {
        state.filter.data = action.payload.data;
        state.filter.total = action.payload.total;
      }
    });
    builder.addCase(onFetchFilterManagementView.rejected, (state, action) => {
      state.filter.network.code = ServiceStatus.failed;
      state.filter.network.error = (action.payload as string) || 'Failed to fetch data';
    });

    // Fetch Customer
    builder.addCase(onFetchCustomerManagementView.pending, (state, action) => {
      state.customer.network.code = ServiceStatus.loading;
      const param = action.meta.arg;
      if (!param || !param.offset || param.offset === 0) {
        state.customer.data = undefined;
      }
    });
    builder.addCase(onFetchCustomerManagementView.fulfilled, (state, action) => {
      state.customer.network.code = ServiceStatus.succeeded;
      const { apiData, params } = action.payload || {};
      if (apiData != null) {
        if (params?.offset && params.offset > 0 && state.customer.data) {
          state.customer.data = [...state.customer.data, ...(apiData.data || [])];
        } else {
          state.customer.data = apiData.data;
        }
        state.customer.total = apiData.total;
      }
    });
    builder.addCase(onFetchCustomerManagementView.rejected, (state, action: any) => {
      if (action.meta.aborted) return;
      state.customer.network.code = ServiceStatus.failed;
      state.customer.network.error = (action.payload as string) || 'Failed to fetch data';
    });
  },
});

// Service
export const onFetchFilterManagementView = createAsyncThunk('management/map/filter', async (params: any = {}, thunkAPI) => {
  try {
    const apiData = await service.fetchFilterManagementView(params);
    return apiData;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const onFetchCustomerManagementView = createAsyncThunk('management/map/customer', async (params: any = {}, thunkAPI) => {
  try {
    const apiData = await service.fetchCustomerManagementView(params, thunkAPI.signal);
    return { apiData, params };
  } catch (error: any) {
    if (axios.isCancel(error)) throw error;
    return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Actions
export const { patch, reset } = slice.actions;

// Export
export default slice.reducer;
